import { useEffect } from 'react';
import { useNavigate, useRouteError, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { hapticsImpactLight } from './utils/haptics';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { useRefreshStudySessionAndContent } from './utils/refreshStudySession';
import { getUserInfo } from './services/auth';
import posthog from 'posthog-js';

function ErrorPage() {
  const error: unknown = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();

  const refreshAndReturnHome = async () => {
    await getUserInfo();
    await refreshStudySessionAndContent();
    navigate('/');
  };

  const escapeHatch = async () => {
    posthog.capture('hard_refresh_clicked');
    Sentry.captureMessage('Hard refresh clicked');
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    });
    window.location.reload();
  };

  // Enhanced error logging to Sentry
  useEffect(() => {
    const logErrorToSentry = () => {
      const timestamp = new Date().toISOString();
      const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Set context for this error
      Sentry.withScope((scope) => {
        // Set error ID for tracking
        scope.setTag('errorId', errorId);
        scope.setTag('errorSource', 'ErrorPage');
        scope.setTag('timestamp', timestamp);

        // Route context
        scope.setContext('route', {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
          key: location.key,
        });

        // Browser context
        scope.setContext('browser', {
          userAgent: navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer,
          timestamp: timestamp,
        });

        // App state context (keys only for privacy)
        scope.setContext('appState', {
          localStorageKeys: Object.keys(localStorage),
          sessionStorageKeys: Object.keys(sessionStorage),
          localStorageCount: localStorage.length,
          sessionStorageCount: sessionStorage.length,
        });

        // Error categorization
        const errorString = String(error);
        let errorCategory = 'unknown';
        let errorSubcategory = '';

        if (isErrorWithStatus(error)) {
          errorCategory = 'http';
          if (error.status === 404) {
            errorSubcategory = 'not-found';
            scope.setTag('errorType', '404');
            scope.setLevel('info'); // 404s are less critical
          } else if (error.status >= 500) {
            errorSubcategory = 'server-error';
            scope.setTag('errorType', 'server-error');
            scope.setLevel('error');
          } else if (error.status >= 400) {
            errorSubcategory = 'client-error';
            scope.setTag('errorType', 'client-error');
            scope.setLevel('warning');
          }
          scope.setTag('httpStatus', error.status.toString());
        } else if (error instanceof Error) {
          errorCategory = 'javascript';
          errorSubcategory = 'runtime-error';
          scope.setTag('errorType', 'javascript-runtime');
          scope.setLevel('error');
        }

        // Special error detection
        if (
          errorString.includes('ChunkLoadError') ||
          errorString.includes('Loading chunk')
        ) {
          errorCategory = 'deployment';
          errorSubcategory = 'chunk-loading';
          scope.setTag('errorType', 'chunk-loading');
          scope.setTag('likelyCause', 'deployment-while-user-active');
          scope.setLevel('warning'); // Usually not critical, just annoying
        }

        if (
          errorString.includes('NetworkError') ||
          errorString.includes('fetch')
        ) {
          errorCategory = 'network';
          errorSubcategory = 'network-error';
          scope.setTag('errorType', 'network');
          scope.setLevel('warning');
        }

        scope.setTag('errorCategory', errorCategory);
        scope.setTag('errorSubcategory', errorSubcategory);

        // Additional error details
        if (error instanceof Error) {
          scope.setContext('errorDetails', {
            name: error.name,
            message: error.message,
            stack: error.stack,
          });
        } else if (isErrorWithStatus(error)) {
          scope.setContext('errorDetails', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
          });
        }

        // Set user context if available (you might want to get this from your auth state)
        scope.setUser({
          id: currentUser?.id || '',
          email: currentUser?.email || '',
        });

        // Capture the error to Sentry
        if (isErrorWithStatus(error) && error.status === 404) {
          // For 404s, capture as a message rather than an exception
          Sentry.captureMessage(`404 Error: ${location.pathname}`, 'info');
        } else {
          // For other errors, capture as an exception
          Sentry.captureException(error);
        }
      });

      // Also log to console in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.group(`🚨 [ERROR ${errorId}] - ${timestamp}`);
        console.log('Raw error object:', error);
        console.log('Location:', location);
        console.log('Error logged to Sentry');
        console.groupEnd();
      }
    };

    logErrorToSentry();
  }, [error, location]);

  function isErrorWithStatus(
    error: unknown
  ): error is { status: number; statusText?: string; message?: string } {
    return typeof error === 'object' && error !== null && 'status' in error;
  }

  useEffect(() => {
    if (isErrorWithStatus(error) && error.status !== 404) {
      const hasRecoveryRefreshed = localStorage.getItem('hasRecoveryRefreshed');
      if (!hasRecoveryRefreshed) {
        // Log recovery attempt to Sentry
        Sentry.addBreadcrumb({
          message: 'Attempting auto-recovery refresh',
          level: 'info',
          category: 'error-recovery',
        });

        localStorage.setItem('hasRecoveryRefreshed', 'true');
        window.location.reload();
      } else {
        // Log that recovery was already attempted
        Sentry.addBreadcrumb({
          message: 'Auto-recovery already attempted, showing error page',
          level: 'warning',
          category: 'error-recovery',
        });

        localStorage.removeItem('hasRecoveryRefreshed');
      }
    }
  }, [error]);

  const renderErrorPage = (error: unknown) => {
    if (isErrorWithStatus(error) && error.status === 404) {
      return (
        <div className="page-not-found">
          <h1>Page not found</h1>
          <p>Unfortunately, the page you are looking for cannot be found.</p>
          <button
            className="button"
            onClick={() => {
              // Log user interaction to Sentry
              Sentry.addBreadcrumb({
                message: 'User clicked "Return Home" from 404 page',
                level: 'info',
                category: 'user-interaction',
              });

              refreshAndReturnHome();
              hapticsImpactLight();
            }}
          >
            Return Home
          </button>

          <div className="escape-hatch">
            <button
              className="escape-hatch-button"
              onClick={() => {
                // Log escape hatch usage to Sentry
                Sentry.addBreadcrumb({
                  message:
                    'User clicked escape hatch from 404 page - clearing all data',
                  level: 'warning',
                  category: 'user-interaction',
                });

                escapeHatch();
                hapticsImpactLight();
              }}
            >
              Refresh not working? Clear data & log out
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="unexpected-error">
          <h1>Oops!</h1>
          <p>Sorry, an unexpected error has occurred.</p>
          <button
            className="button"
            onClick={() => {
              // Log user interaction to Sentry
              Sentry.addBreadcrumb({
                message:
                  'User clicked "Refresh & Return Home" - clearing app state',
                level: 'info',
                category: 'user-interaction',
              });
              refreshAndReturnHome();
              hapticsImpactLight();
            }}
          >
            Return Home
          </button>

          <div className="escape-hatch">
            <button
              className="escape-hatch-button"
              onClick={() => {
                // Log escape hatch usage to Sentry
                Sentry.addBreadcrumb({
                  message:
                    'User clicked escape hatch from error page - clearing all data',
                  level: 'warning',
                  category: 'user-interaction',
                });
                escapeHatch();
                hapticsImpactLight();
              }}
            >
              Not working? Hard refresh
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div id="error-page" className="error-page">
      {renderErrorPage(error)}
    </div>
  );
}

export default ErrorPage;
